// ABOUTME: Login page with Auth0 integration.
// ABOUTME: Provides authentication entry point for users.

import { useEffect } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LogIn, Shield } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

export function LoginPage() {
  const { isAuthenticated, isLoading, login } = useAuth();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo') || '/';

  // Store returnTo in sessionStorage for post-login redirect
  useEffect(() => {
    if (returnTo && returnTo !== '/') {
      sessionStorage.setItem('auth_return_to', returnTo);
    }
  }, [returnTo]);

  // If already authenticated, redirect to intended destination
  if (isAuthenticated) {
    const storedReturnTo = sessionStorage.getItem('auth_return_to');
    sessionStorage.removeItem('auth_return_to');
    return <Navigate to={storedReturnTo || returnTo} replace />;
  }

  // Show loading while checking auth state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-50">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const handleLogin = () => {
    login();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-50 p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="card p-12 shadow-lg max-w-md w-full"
      >
        <div className="flex justify-center mb-8">
          <div className="w-16 h-16 rounded-sm bg-primary-50 flex items-center justify-center">
            <Shield className="w-8 h-8 text-primary-600" />
          </div>
        </div>

        <h1 className="text-3xl font-semibold text-surface-900 mb-2 text-center">
          Welcome to Oleum BI
        </h1>
        <p className="text-surface-500 text-center mb-8">
          Sign in to access your investigations and reports
        </p>

        <Button
          onClick={handleLogin}
          data-testid="auth0-login-button"
          className="w-full"
          size="lg"
          leftIcon={<LogIn className="w-5 h-5" />}
        >
          Get started
        </Button>

        <p className="text-surface-400 text-sm text-center mt-6">
          Secure authentication powered by Auth0
        </p>
      </motion.div>
    </div>
  );
}

export default LoginPage;
